import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IconCalendarStats,
  IconCash,
  IconCircleCheckFilled,
  IconClipboardCheck,
  IconCoins,
  IconDeviceDesktop,
  IconDownload,
  IconLink,
  IconMapPin,
  IconPackage,
  IconPlug,
  IconReceipt,
  IconX,
  IconXboxXFilled
} from '@tabler/icons-react';
import { toPng } from 'html-to-image';
import type { Branch, DailySale } from './domain';
import { thaiDisplayDate } from './report';
import './reportSlip.css';

const money = new Intl.NumberFormat('th-TH', { maximumFractionDigits: 2 });

interface ReportSlipProps {
  branches: Branch[];
  sales: DailySale[];
  date: string;
  total: number;
  regionTotals: Array<[string, number]>;
  onClose: () => void;
  onNotify: (msg: string) => void;
}

// 7 checklist items
const CHECKLIST_ITEMS = [
  { key: 'paidComplete', label: 'โอน', icon: IconCash },
  { key: 'posComplete', label: 'POS', icon: IconDeviceDesktop },
  { key: 'billComplete', label: 'บิล', icon: IconReceipt },
  { key: 'stockAttached', label: 'สต๊อก', icon: IconPackage },
  { key: 'salesLinkUpdated', label: 'ลิงก์', icon: IconLink },
  { key: 'dailyInspectionAttached', label: 'ตรวจ', icon: IconClipboardCheck },
  { key: 'unpluggedPhotoConfirmed', label: 'ปลั๊ก', icon: IconPlug }
] as const;

export function ReportSlipActions({
  branches,
  sales,
  date,
  total,
  regionTotals,
  onClose,
  onNotify
}: ReportSlipProps) {
  const [isExporting, setIsExporting] = useState(false);
  const slipRef = useRef<HTMLDivElement>(null);

  const activeBranches = branches
    .filter((b) => b.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const salesMap = new Map<string, DailySale>(
    sales.filter((s) => s.salesDate === date).map((s) => [s.branchId, s])
  );

  const thaiDateStr = thaiDisplayDate(date);

  const handleExport = async () => {
    if (isExporting) return;
    if (!slipRef.current) return;

    try {
      setIsExporting(true);

      const dataUrl = await toPng(slipRef.current, {
        cacheBust: true,
        pixelRatio: 2, // 2x crisp retina resolution
        quality: 0.95,
        backgroundColor: '#ffffff',
        skipFonts: true,
        width: 860
      });

      const fileName = `daily-sales-${date}.png`;

      // Direct download immediately
      const link = document.createElement('a');
      link.download = fileName;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      onNotify('บันทึกรูปภาพเรียบร้อยแล้ว');
    } catch (error) {
      console.error('Failed to export slip image:', error);
      onNotify('ไม่สามารถบันทึกรูปภาพได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {/* Dual action buttons in the modal dock */}
      <div className="slip-dock-actions">
        <button
          type="button"
          className="slip-export-btn"
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? (
            <>
              <div className="slip-spinner" />
              <span>กำลังสร้างรูปภาพ...</span>
            </>
          ) : (
            <>
              <IconDownload />
              <span>บันทึกรูปภาพ</span>
            </>
          )}
        </button>

        <button
          type="button"
          className="slip-close-btn"
          onClick={onClose}
          disabled={isExporting}
        >
          <IconX />
          <span>ปิด</span>
        </button>
      </div>

      {/* Off-screen fixed canvas for rendering authentic receipt (Portaled to document.body to avoid parent mobile constraints) */}
      {typeof document !== 'undefined' &&
        createPortal(
          <div className="slip-export-stage" aria-hidden="true">
            <div className="slip-render-canvas" ref={slipRef}>
          {/* Slip Header */}
          <div className="slip-render-head">
            <div className="slip-head-left">
              <span className="slip-system-tag">DAILY SALES REPORT</span>
              <div className="slip-title-group">
                <h1>สรุปยอดขายและตรวจงานประจำวัน</h1>
                <p>รายงานตรวจสอบความเรียบร้อยและยอดขายสาขา</p>
              </div>
            </div>

            <div className="slip-head-right">
              <div className="slip-date-box">
                <IconCalendarStats />
                <span>ประจำวันที่ {thaiDateStr}</span>
              </div>
            </div>
          </div>

          {/* Formal Audit Table for Branches & Checklists */}
          <table className="slip-formal-table">
            <thead>
              <tr>
                <th className="col-branch">สาขา / ภาค</th>
                {CHECKLIST_ITEMS.map((item) => (
                  <th key={item.key} className="col-check">
                    {item.label}
                  </th>
                ))}
                <th className="col-status">สถานะ</th>
                <th className="col-amount">ยอดขาย (บาท)</th>
              </tr>
            </thead>
            <tbody>
              {activeBranches.map((branch, index) => {
                const sale = salesMap.get(branch.id);
                const amount = sale?.salesAmount ?? 0;
                const passedCount = CHECKLIST_ITEMS.filter(
                  (item) => Boolean(sale?.[item.key as keyof DailySale])
                ).length;
                const isAllPassed = passedCount === CHECKLIST_ITEMS.length;

                return (
                  <tr key={branch.id} className={index % 2 === 1 ? 'row-alt' : ''}>
                    <td className="col-branch">
                      <div className="branch-title-cell">
                        <span className="branch-index">{String(index + 1).padStart(2, '0')}.</span>
                        <span className="branch-name">สาขา{branch.name}</span>
                        <span className="branch-region">({branch.region})</span>
                      </div>
                    </td>
                    {CHECKLIST_ITEMS.map((item) => {
                      const isOk = Boolean(sale?.[item.key as keyof DailySale]);
                      return (
                        <td key={item.key} className="col-check">
                          {isOk ? (
                            <IconCircleCheckFilled className="chk-icon-filled ok" title={item.label} />
                          ) : (
                            <IconXboxXFilled className="chk-icon-filled no" title={item.label} />
                          )}
                        </td>
                      );
                    })}
                    <td className="col-status">
                      <span className={`status-pill ${isAllPassed ? 'complete' : 'incomplete'}`}>
                        {passedCount}/{CHECKLIST_ITEMS.length} {isAllPassed ? 'ครบ' : 'ไม่ครบ'}
                      </span>
                    </td>
                    <td className="col-amount">
                      <strong className={`amt-text ${amount > 0 ? 'positive' : 'zero'}`}>
                        {money.format(amount)}
                      </strong>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="slip-table-total-row">
                <td colSpan={8} className="total-label-cell">
                  รวมยอดขายสุทธิทั้งหมด ({activeBranches.length} สาขา)
                </td>
                <td className="total-status-cell">
                  {activeBranches.filter(
                    (b) => CHECKLIST_ITEMS.every(i => Boolean(salesMap.get(b.id)?.[i.key as keyof DailySale]))
                  ).length}/{activeBranches.length} ครบ
                </td>
                <td className="total-amount-cell">
                  <strong className="grand-total-val">฿{money.format(total)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>

          {/* Region Breakdown - Formal Document Summary */}
          <div className="slip-region-section">
            <div className="slip-region-header">
              <IconMapPin className="region-pin-icon" />
              <span>สรุปยอดขายแยกตามรายภูมิภาค</span>
            </div>
            <div className="slip-region-list">
              {regionTotals.map(([region, amount]) => (
                <div key={region} className="slip-region-item">
                  <span className="slip-region-label">{region}</span>
                  <span className="slip-region-divider" />
                  <span className="slip-region-val">฿{money.format(amount)}</span>
                </div>
              ))}
            </div>
          </div>

            {/* Receipt Footer */}
            <div className="slip-receipt-footer">
              <span className="slip-easter-egg">©Hello World</span>
              <span>เวลาออกรายงาน: {new Date().toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
